import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

import type { EAmplitudeScale } from '@web/entities/measurement'
import type { EUnitType } from '@web/entities/measurement/model/enums/unit.enum'
import { useDispatchedActions } from '@web/shared/hooks/use-dispatched-actions.hook'
import type { TColorHex } from '@web/shared/types/string-types.type'

import type { IChartPoint } from './types/chart.type'

export type TExpressSection = {
  fl: number
  fh: number
  fill: TColorHex
}

export interface ISectionsResultData {
  skz: number
  am: number
  fm: number
}

export type TExpressSectionResult = {
  title: string
  unitType: EUnitType
  amplitudeMode: EAmplitudeScale
  data: ISectionsResultData[]
}

export interface IInitialSectionsState {
  sections: TExpressSection[]
  results: TExpressSectionResult[]
  isSectionExpressOpen: boolean
  chartSpectrum: IChartPoint[] | null
}

const initialState: IInitialSectionsState = {
  sections: [],
  results: [],
  isSectionExpressOpen: false,
  chartSpectrum: null
}

export const expressSectionSlice = createSlice({
  name: 'express-section',
  initialState,
  reducers: {
    setIsSectionExpressOpen(state, action: PayloadAction<boolean>) {
      state.isSectionExpressOpen = action.payload
    },

    setExpressSection(
      state,
      action: PayloadAction<{ section: TExpressSection; index: number }>
    ) {
      const index = action.payload.index
      state.sections[index] = action.payload.section
    },

    setSectionSpectrum(state, action: PayloadAction<IChartPoint[]>) {
      state.chartSpectrum = action.payload
    },

    pushSection(state, action: PayloadAction<TExpressSection>) {
      state.sections.push(action.payload)
    },

    addExpressSectionResult(
      state,
      action: PayloadAction<TExpressSectionResult>
    ) {
      const foundIndex = state.results.findIndex(
        (result: TExpressSectionResult) => result.title === action.payload.title
      )

      if (foundIndex === -1) {
        state.results.push(action.payload)
      } else {
        state.results[foundIndex] = {
          ...state.results[foundIndex],
          ...action.payload
        }
      }
    },

    resetSectionsResults(state) {
      state.results = []
    },

    resetSectionsBars(state) {
      state.sections = []
    }
  }
})

export const expressSectionReducer = expressSectionSlice.reducer
export const useExpressSectionActions = () =>
  useDispatchedActions(expressSectionSlice.actions)
